import { useEffect } from 'react';

/**
 * Unsaved-change guards for the top bar's Back/Forward arrows.
 *
 * A page with unsaved edits (the form set-up editor, the landing-page editor,
 * the Transact form fill editor) registers a guard while it is dirty. The
 * arrows ask `pendingNavigationGuard()` first and, when it returns a message,
 * confirm once with that message before moving — then step with
 * `ignoreBlocker: true`, so the page's own `useBlocker` does not ask a second
 * time. Native Back/Forward (mouse buttons, trackpad swipes) still go through
 * the page's `useBlocker`.
 *
 * The in-page "Back to …" links (historyPosition.ts `goBackTo`) also ask it:
 * while a guard is active they never step back more than one entry at once
 * (TanStack rolls a cancelled multi-entry pop back by only one entry), they
 * open the list with a push instead. So every `useBlocker` that can cancel a
 * step must, for as long as it can block, register a guard here or (like
 * MailComposeModal while sending) refuse `requestWorkspaceNavigation`.
 *
 * MailComposeModal keeps using `requestWorkspaceNavigation` (it refuses while
 * a send or upload is in flight; it is not an unsaved-change confirm).
 *
 * Module-level and tiny: TopBar is in the always-loaded entry chunk.
 */

interface GuardEntry {
  message: string;
}

const guards: GuardEntry[] = [];

/** Register a guard; returns the function that removes it. */
export function registerNavigationGuard(message: string): () => void {
  const entry: GuardEntry = { message };
  guards.push(entry);
  return () => {
    const at = guards.indexOf(entry);
    if (at >= 0) guards.splice(at, 1);
  };
}

/** Keep a guard registered while `active` (e.g. while the editor is dirty). */
export function useNavigationGuard(active: boolean, message: string): void {
  useEffect(() => {
    if (!active) return undefined;
    return registerNavigationGuard(message);
  }, [active, message]);
}

/** The first active guard's message (registration order), or null when nothing is guarded. */
export function pendingNavigationGuard(): string | null {
  return guards[0]?.message ?? null;
}
